import React, { useEffect, useState } from "react";
import { Trophy, Heart, Crown, Medal } from "lucide-react";
import api from "../lib/api";

/**
 * ContestLeaderboardPanel — public ranking of contest finalists by votes.
 *
 * Pulls from GET /api/contest/leaderboard. Renders a compact ranked list
 * with a trophy/medal/crown icon for the top three and optional social
 * links. Hidden entirely if the list is empty.
 */
export default function ContestLeaderboardPanel() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/contest/leaderboard?limit=10")
      .then((r) => { if (mounted) setRows(r.data || []); })
      .catch(() => { if (mounted) setRows([]); });
    return () => { mounted = false; };
  }, []);

  if (rows == null) return null;
  if (rows.length === 0) return null;

  return (
    <div data-testid="contest-leaderboard" className="bg-gradient-to-br from-fuchsia-500/10 to-tv-accent/10 border border-white/10 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-5 h-5 text-tv-accent" />
        <h3 className="font-display font-black text-xl">Leaderboard</h3>
        <span className="text-white/40 text-xs ml-auto">Top {rows.length} by votes</span>
      </div>
      <ol className="space-y-1.5">
        {rows.map((r, i) => (
          <li
            key={r.id}
            data-testid={`leader-${r.id}`}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
              i === 0 ? "bg-tv-accent/20 border border-tv-accent/40"
                : i < 3 ? "bg-white/10 border border-white/10"
                : "bg-white/5"
            }`}
          >
            <div className="shrink-0 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
              {i === 0 ? <Crown className="w-4 h-4 text-tv-accent" />
                : i === 1 ? <Medal className="w-4 h-4 text-white/80" />
                : i === 2 ? <Medal className="w-4 h-4 text-amber-500" />
                : <span className="text-[11px] font-black text-white/60">{i + 1}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold truncate">
                {r.stage_name}
                {r.status === "winner" && <span className="ml-2 text-[10px] bg-tv-accent text-black font-black uppercase px-1.5 py-0.5 rounded">WINNER</span>}
                {r.status === "finalist" && <span className="ml-2 text-[10px] bg-fuchsia-500 text-black font-black uppercase px-1.5 py-0.5 rounded">FINALIST</span>}
              </div>
              <div className="text-white/50 text-xs truncate">
                {r.city}
                {r.genres?.length > 0 && <> · {r.genres.slice(0, 3).join(", ")}</>}
              </div>
            </div>
            <div className="shrink-0 inline-flex items-center gap-1 text-fuchsia-300 font-black text-sm">
              <Heart className="w-3.5 h-3.5 fill-fuchsia-400" /> {r.votes_count || 0}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
